<script setup lang="ts">
  import { computed, onBeforeUnmount, ref, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { Loader2 } from 'lucide-vue-next';
  import { useCustomToast, setBreadcrumbLabel, clearBreadcrumbLabel } from '@/shared/lib';
  import { RecordFormViewer } from '@/widgets/record-form-viewer';
  import type { FormStructure, MatrixQueueStructure } from '@/features/FormBuilder';
  import { getRecordViewStageConfig } from '../config';

  const route = useRoute();
  const router = useRouter();
  const { showErrorToast } = useCustomToast();

  const stageConfig = computed(() => getRecordViewStageConfig(route.meta.recordViewStage));

  const record = ref<MatrixQueueStructure | null>(null);
  const isLoading = ref(true);
  let latestRequest = 0;

  const matrixId = computed(() => Number(route.params.id));

  const viewerStructure = computed<FormStructure | null>(() => {
    if (!record.value) return null;
    return {
      elements: record.value.matrixStructure.elements,
      version: record.value.matrixStructure.version,
      metadata: record.value.matrixStructure.metadata,
    };
  });

  async function fetchRecord(): Promise<void> {
    // Only the newest request may write. A slow answer for the Registration the user just left
    // would otherwise land on top of the one on screen, code, form and anchors included.
    const request = ++latestRequest;
    isLoading.value = true;
    try {
      // An item of somebody else, a draft and an unknown id all answer the same way here, so
      // every failure takes the single exit: warn and hand the user back to the queue listing.
      const result = await stageConfig.value.fetchStructure(matrixId.value);
      if (request !== latestRequest) return;
      record.value = result;
      setBreadcrumbLabel(result.prefix);
    } catch {
      if (request !== latestRequest) return;
      // Dropping it matters: the redirect is not awaited, so leaving the previous Registration in
      // place would draw it, breadcrumb included, under the URL that just failed.
      record.value = null;
      showErrorToast({
        title: stageConfig.value.loadErrorTitle,
        message: stageConfig.value.loadErrorMessage,
        type: 'error',
      });
      router.replace(stageConfig.value.listRoute);
    } finally {
      if (request === latestRequest) {
        isLoading.value = false;
      }
    }
  }

  // The router keeps this component alive when only the id changes, so mounting is not enough:
  // without the immediate watch the screen would keep drawing the Registration it opened with.
  watch(matrixId, fetchRecord, { immediate: true });

  onBeforeUnmount(() => {
    clearBreadcrumbLabel();
  });
</script>

<template>
  <div class="flex h-full flex-col">
    <div
      v-if="isLoading"
      class="flex flex-1 items-center justify-center"
      data-testid="record-view-loading"
    >
      <Loader2 class="h-6 w-6 animate-spin text-[var(--theme-secondary-ink)]" />
    </div>

    <!-- Keyed by the Registration so a new one gets a fresh viewer. The stage loads the form into
         the store when it mounts, and the router keeps this page alive when only the id changes,
         so without the key the canvas would keep drawing the previous form. -->
    <RecordFormViewer
      v-else-if="viewerStructure"
      :key="matrixId"
      :structure="viewerStructure"
      :rules-structure="record?.rulesStructure"
    />
  </div>
</template>
